// =============================================================================
// APP — Root Application Component for Le Jean Bart
// =============================================================================
// Main application layout composing:
// SkipToContent → Navbar → Hero → Menu → Ambiance Showcase → About → Gallery → Contact & Map → Footer
// + CookieConsent & LegalModal (Privacy, Terms, Accessibility, Cookie Settings)
// Fully localized across 6 languages (FR, EN, ZH, DE, IT, PT).
// =============================================================================

import { useTranslation } from 'react-i18next';
import { useState, useCallback } from 'react';
import {
  SkipToContent,
  Navbar,
  Hero,
  MenuSection,
  ReviewsSection,
  Footer,
  CookieConsent,
  LegalModal,
  CTAButton,
  LanguageSwitcher,
  DeliveryButtons,
  AtmosphereBackground,
  generateRestaurantSchema,
  type NavLink,
  type Language,
  type LegalModalType,
  type GuideLinkItem,
  type ReservationLinkItem,
  type AtmosphereLayer,
} from '@webzite/core';
import { OpenStreetMapEmbed, GoogleMapsEmbed } from '@webzite/integrations';
import { validateRestaurant, validateMenu } from '@webzite/content-schema';

// Import content data
import restaurantJson from '../content/restaurant.json';
import menuJson from '../content/menu.json';
import siteConfig from '../site.config.js';

// Validate content at runtime
const restaurant = validateRestaurant(restaurantJson);
const menu = validateMenu(menuJson);

export function App() {
  const { t, i18n } = useTranslation();
  const [activeModal, setActiveModal] = useState<LegalModalType>(null);

  // --- Navigation links ---
  const navLinks: NavLink[] = [
    { id: 'menu', label: t('nav.menu'), href: '#menu' },
    { id: 'ambiance', label: t('nav.about'), href: '#about' },
  ];

  if (restaurant.features.gallery && restaurant.gallery.length > 0) {
    navLinks.push({ id: 'gallery', label: t('nav.gallery'), href: '#gallery' });
  }

  if (restaurant.features.reviews && restaurant.reviews) {
    navLinks.push({ id: 'reviews', label: t('reviews.heading', 'Avis'), href: '#reviews' });
  }

  navLinks.push({ id: 'contact', label: t('nav.contact'), href: '#contact' });

  // --- Languages ---
  const languages: Language[] = siteConfig.locales.map((code) => ({
    code,
    label: code.toUpperCase(),
  }));

  const handleLanguageChange = useCallback(
    (locale: string) => {
      void i18n.changeLanguage(locale);
    },
    [i18n],
  );

  const reservationUrl = siteConfig.reservations.bookingUrl ?? `tel:${restaurant.phone.replace(/\s+/g, '')}`;
  const localizedTagline = t('hero.tagline', { defaultValue: restaurant.tagline });
  const localizedStory = restaurant.about
    ? t('about.story', { defaultValue: restaurant.about.story })
    : undefined;

  // Gastronomic guides & reviews references
  const guideLinks: GuideLinkItem[] = [
    {
      label: 'Google Maps (Avis & Note Le Jean Bart)',
      url: 'https://www.google.com/maps/place/Le+Jean+Bart/@48.8129148,2.3160239,17z/data=!3m1!4b1!4m6!3m5!1s0x47e670fe5c7d0d77:0x7754c70f656f723f!8m2!3d48.8129148!4d2.3160239!16s%2Fg%2F1tdlglxl!5m1!1e2?entry=ttu&g_ep=EgoyMDI2MDkwMi4wIKXMDSoASAFQAw%3D%3D',
    },
    {
      label: 'TripAdvisor (Avis & Note Le Jean Bart)',
      url: 'https://www.tripadvisor.fr/Restaurant_Review-g227893-d15596957-Reviews-Le_Jean_Bart-Montrouge_Hauts_de_Seine_Ile_de_France.html',
    },
  ];

  const reservationLinks: ReservationLinkItem[] = [
    {
      label: `${restaurant.phone}`,
      url: `tel:${restaurant.phone.replace(/\s+/g, '')}`,
    },
  ];

  // Dynamic atmospheric background layers that crossfade as user scrolls
  const atmosphereLayers: AtmosphereLayer[] = [
    {
      sectionId: 'hero',
      src: '/images/hero.png',
      alt: 'Façade rouge vermillon et auvent emblématique du Jean Bart à Montrouge',
    },
    {
      sectionId: 'menu',
      src: '/images/dish-couscous.png',
      alt: 'Couscous royal maison généreux et parfumé aux épices douces',
    },
    {
      sectionId: 'ambiance-showcase',
      src: '/images/interior-dining.png',
      alt: 'Comptoir demi-lune en zinc poli, fresques à la craie et ambiance conviviale',
    },
    {
      sectionId: 'about',
      src: '/images/interior-terrasse.png',
      alt: 'Terrasse de trottoir animée avec tonneaux mange-debout sur Avenue de la République',
    },
    {
      sectionId: 'gallery',
      src: '/images/dish-tartare.png',
      alt: 'Tartare de bœuf frais préparé au couteau et frites maison',
    },
    {
      sectionId: 'reviews',
      src: '/images/dish-burger.png',
      alt: 'Burger gourmand artisanal Le Jean Bart avec cheddar fondu',
    },
    {
      sectionId: 'contact',
      src: '/images/hero.png',
      alt: 'Accueil chaleureux au Jean Bart à Montrouge',
    },
  ];

  return (
    <>
      {/* Skip-to-content link for keyboard accessibility */}
      <SkipToContent />

      {/* Dynamic scrolling atmosphere background crossfading through authentic interiors */}
      <AtmosphereBackground layers={atmosphereLayers} />

      {/* Navigation bar (glassmorphic dark velvet styling, sticky on scroll) */}
      <Navbar
        siteName={restaurant.name}
        links={navLinks}
        ctaText={restaurant.features.reservations ? t('nav.reserve') : undefined}
        ctaHref={restaurant.features.reservations ? reservationUrl : undefined}
        languageSwitcher={
          <LanguageSwitcher
            currentLocale={i18n.language}
            languages={languages}
            onLanguageChange={handleLanguageChange}
          />
        }
      />

      {/* Main content */}
      <main id="main-content">
        {/* Hero section with high-resolution Parisian bistro interior */}
        <Hero
          headline={restaurant.name}
          tagline={localizedTagline}
          backgroundImage="/images/hero.png"
          backgroundAlt="Façade rouge vermillon et terrasse animée du bistrot Le Jean Bart à Montrouge"
          transparentBackground={true}
          primaryCTA={
            restaurant.features.reservations ? (
              <CTAButton as="link" href={reservationUrl} variant="primary">
                {t('hero.cta.reserve')}
              </CTAButton>
            ) : undefined
          }
          secondaryCTA={
            <CTAButton as="link" href="#menu" variant="secondary">
              {t('hero.cta.menu')}
            </CTAButton>
          }
        />

        {/* Menu section (starters, bistro mains, couscous, desserts, drinks) */}
        <MenuSection
          menu={menu}
          currency={siteConfig.currency}
          locale={i18n.language}
          heading={t('menu.heading')}
          allergenNote={t('menu.allergens.note')}
        />

        {/* Ambiance showcase strip — rolling interior view as user scrolls down */}
        <section className="section ambiance-section" id="ambiance-showcase" aria-label="Ambiance du restaurant">
          <div className="container">
            <h2 className="text-center">{t('about.heading')}</h2>
            <p className="text-center text-label">
              Montrouge · 134 Avenue de la République
            </p>

            <div className="ambiance-grid">
              <div className="ambiance-card">
                <img
                  src="/images/interior-dining.png"
                  alt="Comptoir demi-lune en zinc poli, fresques à la craie et ambiance conviviale du Jean Bart"
                  className="ambiance-card-image"
                  loading="lazy"
                />
                <div className="ambiance-card-overlay">
                  <h3 className="ambiance-card-title">Le Comptoir & L'Ardoise</h3>
                  <p className="ambiance-card-text">Comptoir demi-lune en zinc poli, fresques à la craie et esprit bistrot authentique</p>
                </div>
              </div>

              <div className="ambiance-card">
                <img
                  src="/images/interior-terrasse.png"
                  alt="Terrasse de trottoir avec tables de bistrot et tonneaux mange-debout"
                  className="ambiance-card-image"
                  loading="lazy"
                />
                <div className="ambiance-card-overlay">
                  <h3 className="ambiance-card-title">La Terrasse & Les Tonneaux</h3>
                  <p className="ambiance-card-text">Esprit bistrot de quartier convivial avec tonneaux mange-debout et tables au soleil</p>
                </div>
              </div>

              <div className="ambiance-card">
                <img
                  src="/images/dish-couscous.png"
                  alt="Couscous royal maison généreux et parfumé aux épices douces"
                  className="ambiance-card-image"
                  loading="lazy"
                />
                <div className="ambiance-card-overlay">
                  <h3 className="ambiance-card-title">Couscous Maison & Cuisine Fraîche</h3>
                  <p className="ambiance-card-text">Cuisine 100% maison, fameux couscous royal parfumé et plats du jour du marché</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* About / Story section */}
        {restaurant.about && (
          <section className="section about-section" id="about" aria-labelledby="about-heading">
            <div className="container container-narrow">
              <div className="about-card text-center">
                <h2 id="about-heading">{t('about.heading')}</h2>
                <p className="about-story-text">{localizedStory}</p>
                {restaurant.about.chefName && (
                  <p className="about-chef-signature">— {restaurant.about.chefName}</p>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Gallery Section */}
        {restaurant.features.gallery && restaurant.gallery.length > 0 && (
          <section className="section gallery-section" id="gallery" aria-labelledby="gallery-heading">
            <div className="container">
              <h2 id="gallery-heading" className="text-center">
                {t('gallery.heading')}
              </h2>
              <div className="gallery-grid-layout">
                {restaurant.gallery.map((img, idx) => (
                  <div key={idx} className="gallery-card-item">
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="lazy"
                      className="gallery-card-image"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Google Maps & Verified Customer Reviews Section (RGPD compliant, top-N filtered) */}
        {restaurant.features.reviews && restaurant.reviews && (
          <ReviewsSection restaurant={restaurant} maxItems={3} minRating={4.5} />
        )}

        {/* Contact, Reservations & Map section */}
        <section className="section contact-section" id="contact" aria-labelledby="contact-heading">
          <div className="container">
            <h2 id="contact-heading" className="text-center">
              {t('contact.heading')}
            </h2>

            <div className="contact-grid-layout">
              {/* Contact Information & Localized Hours */}
              <div className="contact-info-card">
                <h3>{t('contact.address')}</h3>
                <address className="contact-address-box">
                  <p className="contact-street">{restaurant.address.streetAddress}</p>
                  <p className="contact-locality">
                    {restaurant.address.postalCode} {restaurant.address.addressLocality}
                  </p>
                  <p className="contact-detail-row">
                    <strong>{t('contact.phone')}:</strong>{' '}
                    <a href={`tel:${restaurant.phone.replace(/\s+/g, '')}`}>{restaurant.phone}</a>
                  </p>
                  <p>
                    <strong>{t('contact.email')}:</strong>{' '}
                    <a href={`mailto:${restaurant.email}`}>{restaurant.email}</a>
                  </p>
                </address>

                {/* Online Reservations CTAs */}
                {restaurant.features.reservations && (
                  <div className="contact-reservations-wrapper">
                    <h4>{t('nav.reserve')}</h4>
                    <div className="contact-reservations-buttons">
                      <CTAButton as="link" href={reservationUrl} variant="primary">
                        📞 {restaurant.phone}
                      </CTAButton>
                    </div>
                  </div>
                )}

                <h3 style={{ marginBlockStart: 'var(--space-8)' }}>{t('contact.hours')}</h3>
                <ul className="contact-hours-list" role="list">
                  {restaurant.hours.map((spec, i) => {
                    const localizedDays = spec.dayOfWeek
                      .map((d) => t(`days.${d.toLowerCase()}`, { defaultValue: d }))
                      .join(', ');

                    return (
                      <li key={i} className="contact-hours-item">
                        <strong>{localizedDays}</strong>: {spec.opens} – {spec.closes}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Interactive Map Embed with Localized Labels */}
              <div className="contact-map-card">
                <h3>{t('contact.map', 'Plan')}</h3>
                <div className="contact-map-wrapper">
                  {siteConfig.maps.provider === 'google' ? (
                    <GoogleMapsEmbed
                      embedUrl={siteConfig.maps.embedUrl ?? ''}
                      googleMapsLink={siteConfig.maps.googleMapsLink}
                      title={restaurant.name}
                      loadButtonText={t('map.click')}
                      openGoogleMapsText={t('map.open.google')}
                    />
                  ) : (
                    <OpenStreetMapEmbed
                      latitude={restaurant.geo.latitude}
                      longitude={restaurant.geo.longitude}
                      address={`${restaurant.address.streetAddress}, ${restaurant.address.postalCode} ${restaurant.address.addressLocality}`}
                      embedUrl={siteConfig.maps.embedUrl}
                      googleMapsLink={siteConfig.maps.googleMapsLink}
                      appleMapsLink={siteConfig.maps.appleMapsLink}
                      title={restaurant.name}
                      loadButtonText={t('map.click')}
                      openGoogleMapsText={t('map.open.google')}
                      openAppleMapsText={t('map.open.apple')}
                      variant="white"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Delivery buttons (if enabled) */}
        {siteConfig.delivery.enabled && siteConfig.delivery.platforms.length > 0 && (
          <section className="section text-center">
            <div className="container">
              <DeliveryButtons
                platforms={siteConfig.delivery.platforms}
                heading={t('delivery.heading')}
              />
            </div>
          </section>
        )}
      </main>

      {/* Footer (Localized hours, tagline, reservation links, guide links, legal links, developer attribution) */}
      <Footer
        restaurant={restaurant}
        tagline={localizedTagline}
        developer={siteConfig.developer}
        reservationLinks={reservationLinks}
        guideLinks={guideLinks}
        secondaryLinks={[
          { label: t('nav.menu'), href: '#menu' },
          { label: t('nav.about'), href: '#about' },
          { label: t('nav.contact'), href: '#contact' },
        ]}
        onOpenLegal={(type) => setActiveModal(type)}
        onOpenCookieSettings={() => setActiveModal('cookies')}
        labels={{
          hours: t('footer.hours'),
          contact: t('footer.contact'),
          quickLinks: t('footer.quicklinks'),
          guides: t('footer.guides'),
          privacy: t('footer.privacy'),
          terms: t('footer.terms'),
          accessibility: t('footer.accessibility'),
          cookieSettings: t('footer.cookies'),
          allRightsReserved: t('footer.rights'),
          craftedBy: t('footer.craftedBy'),
        }}
        deliveryButtons={
          siteConfig.delivery.enabled ? (
            <DeliveryButtons platforms={siteConfig.delivery.platforms} />
          ) : undefined
        }
      />

      {/* Legal & Cookie Settings Modals */}
      <LegalModal
        type={activeModal}
        onClose={() => setActiveModal(null)}
        restaurant={restaurant}
        locale={i18n.language}
      />

      {/* Cookie consent banner (GDPR) */}
      {(siteConfig.region === 'eu' || siteConfig.region === 'uk') && (
        <CookieConsent
          heading={t('cookies.heading')}
          description={t('cookies.description')}
          acceptText={t('cookies.accept')}
          rejectText={t('cookies.reject')}
          onOpenSettings={() => setActiveModal('cookies')}
        />
      )}

      {/* JSON-LD structured data for SEO & AI with developer creator attribution */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(
            generateRestaurantSchema(
              restaurant,
              restaurant.url ??
              (typeof window !== 'undefined'
                ? window.location.origin
                : 'https://localhost:3000'),
              siteConfig.developer,
            ),
          ),
        }}
      />
    </>
  );
}
